import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, User, Clock, ChevronRight } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  readTime: string;
  content: React.ReactNode;
}

// Helper component for checkmarks
const CheckCircleIcon = () => <div className="text-orange-500 mt-1"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>;

const Blog: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const posts: BlogPost[] = [
    {
      id: 1,
      title: "How to Turn Your Corporate Skills Into a Profitable Business",
      date: "October 12, 2025",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1000",
      excerpt: "Your 9-5 experience is more valuable than you think. Learn the 'Translation Matrix' to package your corporate expertise into a high-ticket offer.",
      content: (
        <div className="space-y-6 text-lg text-stone-700 leading-relaxed">
          <p>
            One of the biggest myths aspiring female solopreneurs believe is that they need to learn an entirely new skill set to start a business.
            The truth? You are likely sitting on a goldmine of experience that clients are ready to pay for today.
          </p>
          <p>
            I see this constantly. Take Sarah, a former Senior Project Manager. She thought she had to learn "digital marketing" to start an agency. 
            In reality, her ability to manage complex timelines, stakeholders, and budgets was exactly what visionary CEOs were desperate for. She didn't need to be a marketer; she needed to be an <span className="font-bold italic">Integrator</span>.
          </p>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">The Corporate-to-Consultant Translation Matrix</h3>
          <p>The key isn't changing what you do; it's changing how you describe it.</p>
          
          <ul className="space-y-4 bg-white p-6 rounded-lg border border-orange-100">
            <li className="flex flex-col md:flex-row gap-2 md:items-center">
              <span className="font-bold text-stone-500 line-through">Corporate Speak: "I manage stakeholders."</span>
              <ArrowRight className="hidden md:block text-orange-500" size={16} />
              <span className="font-bold text-stone-900">Business Offer: "I align your leadership team to execute your vision faster."</span>
            </li>
            <li className="flex flex-col md:flex-row gap-2 md:items-center">
              <span className="font-bold text-stone-500 line-through">Corporate Speak: "I create SOPs."</span>
              <ArrowRight className="hidden md:block text-orange-500" size={16} />
              <span className="font-bold text-stone-900">Business Offer: "I build the operational playbook that allows you to scale."</span>
            </li>
            <li className="flex flex-col md:flex-row gap-2 md:items-center">
              <span className="font-bold text-stone-500 line-through">Corporate Speak: "I do HR onboarding."</span>
              <ArrowRight className="hidden md:block text-orange-500" size={16} />
              <span className="font-bold text-stone-900">Business Offer: "I help companies retain top talent and reduce turnover costs."</span>
            </li>
          </ul>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">3 Steps to Package Your Skills</h3>
          <ol className="list-decimal list-inside space-y-4 ml-2">
            <li><strong>Audit your "Boring" Skills:</strong> Write down everything you do in a day that feels easy to you. What comes naturally to you is often a major pain point for others.</li>
            <li><strong>Identify the ROI:</strong> Don't sell the task (e.g., "I organize files"). Sell the result (e.g., "I save you 10 hours a week so you can focus on sales").</li>
            <li><strong>Validate with Conversations:</strong> Before turning your idea into an offer, talk to 5 people in your target industry and ask, "Is [Problem X] something you struggle with?". Listen for repeated frustrations and unmet needs, those patterns will tell you whether your idea solves a problem people care about and will actually pay for.</li>
          </ol>

          <div className="bg-orange-100 p-8 rounded-xl mt-8 border-l-4 border-orange-500">
            <h4 className="font-serif font-bold text-xl text-stone-900 mb-2">Ready to make the leap?</h4>
            <p className="mb-4">Feeling stuck on translating your specific skills into an offer? Reach out to us and let’s map your business roadmap together.</p>
            <Link to="/contact" className="inline-block bg-stone-900 text-white px-6 py-2 rounded font-medium hover:bg-stone-800 transition-colors">Email Us</Link>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: "How to Define Your Niche When You Have Too Many Ideas",
      date: "October 05, 2025",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=1000",
      excerpt: "Struggling to pick just one direction? Being a 'Jill-of-all-trades' is a strength, not a weakness—if you position it correctly.",
      content: (
        <div className="space-y-6 text-lg text-stone-700 leading-relaxed">
          <p>
            "I can do marketing, but I also like writing, and I'm good at organization." Does this sound like you?
            Many corporate women suffer from what I call <span className="italic">Competence Overload</span>. You are good at so many things that picking one niche feels like you're putting yourself in a box.
          </p>
          <p>
            But here is the hard truth: <strong>If you try to speak to everyone, you speak to no one.</strong>
          </p>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">The "Who, Not What" Shift</h3>
          <p>
            Instead of obsessing over <em>what</em> service to offer (Copywriting? Virtual Assistance? Strategy?), start with <em>who</em> you want to serve.
          </p>
          <p>
            Meet Jessica. She was a marketing generalist. She tried to sell "Marketing Services" to everyone—dentists, bakeries, tech startups. She was exhausted and broke.
          </p>
          <p>
            We pivoted her focus. She loved wellness and yoga. She decided to niche down to <strong>"Marketing Operations for Wellness Studio Owners."</strong>
          </p>
          <p>
            She used all her same skills (email, social, admin), but because she spoke directly to one specific person, she became the "go-to" expert immediately. Her rates doubled.
          </p>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">The Niche Down Checklist</h3>
          <ul className="space-y-3">
             <li className="flex items-start gap-3">
               <CheckCircleIcon />
               <span><strong>Profitability:</strong> Can this group of people actually afford to pay you? (e.g., struggling students vs. established business owners).</span>
             </li>
             <li className="flex items-start gap-3">
               <CheckCircleIcon />
               <span><strong>Accessibility:</strong> Can you easily find them online? (LinkedIn, Instagram, Facebook Groups).</span>
             </li>
             <li className="flex items-start gap-3">
               <CheckCircleIcon />
               <span><strong>Passion (Just enough):</strong> Do you enjoy their problems enough to solve them every day?</span>
             </li>
          </ul>

          <p className="italic bg-white p-4 border-l-2 border-brand-dark my-4">
            Remember: A niche isn't a life sentence. It's just your entry point to the market. You can always expand later.
          </p>

          <div className="bg-orange-100 p-8 rounded-xl mt-8 border-l-4 border-orange-500">
             <h4 className="font-serif font-bold text-xl text-stone-900 mb-2">Still spinning your wheels?</h4>
            <p className="mb-4">If you have 10 ideas and don't know which one will make money, we can help you validate the right one. Reach out to us and let’s narrow down your niche together.</p>
            <Link to="/contact" className="inline-block bg-stone-900 text-white px-6 py-2 rounded font-medium hover:bg-stone-800 transition-colors">Email Us</Link>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "The Minimum Viable Offer (MVO) Explained: Start Small, Earn Fast",
      date: "September 28, 2025",
      readTime: "7 min read",
      image: "https://images.pexels.com/photos/4458554/pexels-photo-4458554.jpeg",
      excerpt: "Don't spend 6 months building a course nobody buys. Learn how to launch an MVO to validate your idea and generate cash flow immediately.",
      content: (
        <div className="space-y-6 text-lg text-stone-700 leading-relaxed">
          <p>
            The biggest mistake I see new entrepreneurs make? <strong>Building the castle before they've sold a single brick.</strong>
          </p>
          <p>
            I once worked with a client, Rachel, who spent 6 months filming a comprehensive video course on "Leadership for New Managers." She bought equipment, edited videos, and built a fancy website.
          </p>
          <p>
            On launch day? Crickets. Zero sales.
          </p>
          <p>
            Why? Because she didn't know what her audience actually wanted. She guessed.
          </p>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">Enter the MVO (Minimum Viable Offer)</h3>
          <p>
            An MVO is the simplest version of your service that delivers the core transformation. It allows you to sell <em>first</em>, and build <em>later</em>.
          </p>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">How to Structure Your MVO</h3>
          <p>Instead of a full course or a 6-month retainer, try this:</p>
          
          <div className="grid md:grid-cols-2 gap-6 my-6">
             <div className="bg-white p-6 rounded shadow-sm border border-stone-100">
                <h4 className="font-bold text-stone-900 mb-2">❌ The Perfectionist Way</h4>
                <ul className="text-sm space-y-2 text-stone-600">
                    <li>• Build a full website</li>
                    <li>• Record 20 videos</li>
                    <li>• Automate email sequences</li>
                    <li>• <strong>Result:</strong> 6 months delay, $0 revenue</li>
                </ul>
             </div>
             <div className="bg-orange-50 p-6 rounded shadow-sm border border-orange-200">
                <h4 className="font-bold text-stone-900 mb-2">✅ The MVO Way</h4>
                <ul className="text-sm space-y-2 text-stone-600">
                    <li>• PDF Outline of what you'll teach</li>
                    <li>• Sell a "Beta Cohort" (4 live Zoom calls)</li>
                    <li>• Manual invoicing</li>
                    <li>• <strong>Result:</strong> Launch in 1 week, immediate cash flow</li>
                </ul>
             </div>
          </div>

          <p>
            By delivering your service live (via consulting or workshops) first, you get real-time feedback. You learn exactly where clients get stuck. You collect testimonials. 
          </p>
          <p>
            <em>Then</em>, once you've validated that people will pay for it, you can build the scalable version.
          </p>

          <h3 className="text-2xl font-serif text-stone-900 pt-4">Your MVO Action Plan</h3>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Pick ONE specific problem you can solve.</li>
            <li>Outline a 4-step process to solve it.</li>
            <li>Offer to solve it for 3 people at a "Beta Price" (50% off) in exchange for a testimonial.</li>
            <li>Deliver it live over Zoom or a simple PDF audit.</li>
          </ol>

          <div className="bg-orange-100 p-8 rounded-xl mt-8 border-l-4 border-orange-500">
             <h4 className="font-serif font-bold text-xl text-stone-900 mb-2">Need help crafting your offer?</h4>
            <p className="mb-4">Don't waste months building something nobody wants. Reach out to us and let’s build your MVO together.</p>
            <Link to="/contact" className="inline-block bg-stone-900 text-white px-6 py-2 rounded font-medium hover:bg-stone-800 transition-colors">Email Us</Link>
          </div>
        </div>
      )
    }
  ];

  const handlePostClick = (post: BlogPost) => {
    setSelectedPost(post);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setSelectedPost(null);
    window.scrollTo(0, 0);
  };

  if (selectedPost) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 animate-fade-in">
        <button onClick={handleBack} className="flex items-center text-stone-500 hover:text-brand-dark mb-8 transition-colors group">
          <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" /> Back to Blog
        </button>
        
        <div className="mb-8">
            <div className="flex items-center gap-4 text-sm text-stone-500 mb-4">
                <span className="flex items-center gap-1"><Calendar size={14}/> {selectedPost.date}</span>
                <span className="flex items-center gap-1"><Clock size={14}/> {selectedPost.readTime}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6 leading-tight">{selectedPost.title}</h1>
            <img 
                src={selectedPost.image} 
                alt={selectedPost.title} 
                className="w-full h-[400px] object-cover rounded-xl shadow-lg mb-8"
            />
        </div>

        <article className="prose prose-stone prose-lg max-w-none">
            {selectedPost.content}
        </article>
      </div>
    );
  }

  return (
    <div className="pb-24 bg-orange-50">
      {/* Header */}
      <div className="bg-stone-900 text-orange-50 py-24 px-6 text-center">
        <h1 className="text-5xl font-serif mb-6">The Productive Folks Blog</h1>
        <p className="text-orange-100/80 max-w-2xl mx-auto text-lg">
          Insights, strategies, and tough love for women building profitable businesses.
        </p>
      </div>

      {/* Post Grid */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map((post) => (
          <div key={post.id} className="bg-white rounded-xl shadow-xl overflow-hidden border border-orange-100 flex flex-col hover:shadow-2xl transition-all duration-300 group cursor-pointer" onClick={() => handlePostClick(post)}>
            <div className="h-48 overflow-hidden">
                <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
            </div>
            <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-xs text-stone-400 mb-4 uppercase tracking-widest font-medium">
                    <span>{post.date}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                </div>
                <h3 className="text-2xl font-serif font-bold text-stone-900 mb-4 group-hover:text-orange-600 transition-colors">{post.title}</h3>
                <p className="text-stone-600 mb-6 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                
                <div className="mt-auto pt-6 border-t border-orange-50 flex items-center justify-between">
                    <span className="text-stone-900 font-bold flex items-center gap-2 group-hover:gap-3 transition-all">
                        Read Article <ChevronRight size={16} className="text-orange-500"/>
                    </span>
                </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Blog;